// schedulePresentationModal.jsx
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import DynamicFormInput from "components/form/form";
import React, { useEffect, useState } from "react";
import { updateLegalResearchRequest } from "services/legalResearchService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { createResearchSchedule } from "services/researchScheduleService";
import { updateLegislativeDrafting } from "services/legislativeService";
import { useTranslation } from "react-i18next";

interface SchedulePresentationModalProps {
    data: any;
    setLegalResearch: Function;
    isOpen: boolean;
    onClose: () => void;
}

const PublicFeedback: React.FC<SchedulePresentationModalProps> = ({
    data: singleData,
    setLegalResearch,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const [employees, setEmployees] = useState([]);
    const contextData = useAuth();

    const proposalSchedule = singleData?.schedules?.find(
        (schedule: any) => schedule.purpose === "proposal_presentation"
    );

    const [formState, setFormState] = useState({
        public_feedback: null as File | null,
        amended_law_file: null as File | null,
    });

    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "public_feedback"
        >
    ) => {
        setFormState((prev) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    if (!isOpen) return null;

    const handleAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        const data = new FormData();

        data.append("public_feedback", formState?.public_feedback ?? "");
        if (singleData?.uploaded_files.find((file: any) => file.file_type === "public_feedback"))
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.public_and_amended_file_submitted.value);

        try {
            const response = await updateLegislativeDrafting(singleData.id, data);

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("LegalResearchForwardedSuccess") || "Legal research forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest") || "Failed to update legal research request.", "error");
        } finally {
            setSubmitting(false);
        }

        onClose(); // Close modal after assignment
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form
                onSubmit={handleAssignSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("PublicFeedback") || "Public Feedback"}
                </h2>

                {/* Remark Section */}
                <div className="mb-4">
                    <DynamicFormInput
                        inputs={[
                            {
                                // No 'value' prop for file inputs
                                type: "file",
                                label: t("FeedbackFile") || "Feedback File",
                                name: "public_feedback",
                                onChange: (file) => handleFileChange(file, "public_feedback"),
                            },
                        ]}
                    />
                </div>
                {/* Assign Button */}
                <div className="text-center mt-6 flex gap-4">
                    <button
                        type="submit"
                        className="bg-[#0A1D39] hover:bg-green-600 text-white font-bold py-3 px-8 rounded-md w-full"
                    >
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Submitting") || "Submitting..."}
                            </div>
                        ) : (
                            t("Submit") || "Submit"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PublicFeedback;